import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import { logger } from '../utils/logger';

/**
 * useVotingStatus
 *
 * Custom hook that loads and interprets voting settings from Firestore.
 * Returns whether voting is currently open based on two factors:
 * 1. Manual override (`votingOpen: true`)
 * 2. Voting timeframe (start ≤ now ≤ end)
 *
 * Also returns:
 * - start & end Date objects
 * - leaderboard visibility toggle
 * - leaderboard display limit
 * - raw votingOpen flag (optional for admin UIs)
 */

export const useVotingStatus = (eventId) => {
  const [isVotingOpen, setIsVotingOpen] = useState(false);
  const [votingOpenFlag, setVotingOpenFlag] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardLimit, setLeaderboardLimit] = useState(5);
  const [resultsAudience, setResultsAudience] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const fetchSettings = async () => {

      setLoading(true);
      try {
        const ref = doc(db, "events", eventId, "settings", "voting"); // ✅ CORRECTED PATH
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();

          const now = new Date();
          const startDate = data.votingStart?.toDate?.() ?? null;
          const endDate = data.votingEnd?.toDate?.() ?? null;

          setStart(startDate);
          setEnd(endDate);
          setVotingOpenFlag(data.votingOpen === true);

          const openByTime =
            startDate instanceof Date &&
            endDate instanceof Date &&
            now >= startDate &&
            now <= endDate;

          const open = data.votingOpen === true || openByTime;
          setIsVotingOpen(open);

          setShowLeaderboard(data.showLeaderboard === true);
          setLeaderboardLimit(typeof data.leaderboardLimit === "number" ? data.leaderboardLimit : 5);
          setResultsAudience(
            typeof data.resultsAudience === "string"
              ? data.resultsAudience.trim().toLowerCase()
              : null
          );
        }
      } catch (err) {
        logger.error("❌ Error fetching voting status:", { value: err });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [eventId]);

  return {
    isVotingOpen,
    start,
    end,
    loading,
    showLeaderboard,
    leaderboardLimit,
    resultsAudience,

    // ✅ Backward compatibility for historical events.
    resolvedResultsAudience:
      resultsAudience || (showLeaderboard ? "voters" : "managers"),

    votingOpenFlag,
  };
};
